// src/views/PlateformesList.js
import React, { useState, useEffect } from 'react';
import { plateformesAPI } from '../api/api';

function PlateformesList() {
  const [plateformes, setPlateformes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPlateforme, setCurrentPlateforme] = useState({ 
    id: null, 
    nom: '', 
    description: '',
    wordpress_url: '',
    wordpress_username: '',
    wordpress_password: '',
    is_active: true
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadPlateformes();

    const handleFocus = () => {
      loadPlateformes();
    };

    window.addEventListener('focus', handleFocus);
    
    // Charger les données à chaque retour sur la page
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadPlateformes = async () => {
    try {
      setLoading(true);
      const data = await plateformesAPI.getAll();
      setPlateformes(data);
    } catch (err) {
      setError('Erreur lors du chargement des plateformes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentPlateforme({ 
      id: null, 
      nom: '', 
      description: '',
      wordpress_url: '',
      wordpress_username: '',
      wordpress_password: '',
      is_active: true 
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (plateforme) => {
    setCurrentPlateforme({ 
      ...plateforme,
      wordpress_url: plateforme.wordpress_url || '',
      wordpress_username: plateforme.wordpress_username || '',
      wordpress_password: plateforme.wordpress_password || '',
      is_active: plateforme.is_active !== false
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette plateforme ?')) {
      try {
        await plateformesAPI.remove(id);
        loadPlateformes(); // Recharger la liste
      } catch (err) {
        setError('Erreur lors de la suppression de la plateforme');
        console.error(err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditing) {
        await plateformesAPI.update(currentPlateforme.id, currentPlateforme);
      } else {
        await plateformesAPI.create(currentPlateforme);
      }
      
      setShowModal(false);
      loadPlateformes(); // Recharger la liste
    } catch (err) {
      setError('Erreur lors de la sauvegarde de la plateforme');
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentPlateforme(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Gestion des plateformes</h2>
          <div className="flex justify-center items-center h-32">
            <p className="text-gray-600">Chargement des plateformes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Gestion des plateformes</h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Gestion des plateformes</h2>
          <button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition"
          >
            + Nouvelle plateforme
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WordPress URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {plateformes.map((plateforme) => (
                <tr key={plateforme.id} className={!plateforme.is_active ? 'bg-gray-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plateforme.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{plateforme.nom}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{plateforme.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {plateforme.wordpress_url || <span className="text-red-500">Non configuré</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {plateforme.is_active ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(plateforme)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(plateforme.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {plateformes.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">Aucune plateforme trouvée</p>
          </div>
        )}
      </div>

      {/* Modal pour créer/modifier une plateforme */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isEditing ? 'Modifier la plateforme' : 'Créer une nouvelle plateforme'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                    <input
                      type="text"
                      name="nom"
                      value={currentPlateforme.nom}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      name="description"
                      value={currentPlateforme.description}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="2"
                    />
                  </div>
                  
                  <div className="border-t pt-4 mt-2">
                    <h4 className="text-sm font-medium text-gray-800 mb-3">Configuration WordPress LearnDash</h4>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        URL WordPress *
                        <span className="ml-2 text-xs text-gray-500">(ex: https://ceosone.qweed.fr)</span>
                      </label>
                      <input
                        type="url"
                        name="wordpress_url"
                        value={currentPlateforme.wordpress_url}
                        onChange={handleChange}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom d'utilisateur WordPress *
                        <span className="ml-2 text-xs text-gray-500">(pour l'API)</span>
                      </label>
                      <input
                        type="text"
                        name="wordpress_username"
                        value={currentPlateforme.wordpress_username}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mot de passe d'application *
                        <span className="ml-2 text-xs text-gray-500">(Généré dans WordPress)</span>
                      </label>
                      <input
                        type="password"
                        name="wordpress_password"
                        value={currentPlateforme.wordpress_password}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        id="is_active"
                        checked={currentPlateforme.is_active}
                        onChange={(e) => setCurrentPlateforme(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                        Plateforme active (synchronisation activée)
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {isEditing ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlateformesList;